import React, { ChangeEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import useUniqueId from '../utils/useUniqueId';

import styles from './SearchInput.module.scss';
import Search from '../Icons/Search';
import TextInput from '../TextInput';
import Button from '../Button';
import LoadingSpinnerNoBits from '../LoadingSpinnerNoBits';
import Note from '../Note';

export interface Props {
  /** CSS classname to apply to the SearchInput container div */
  className?: string;
  /** Label text for the search text input */
  label: string;
  /** Loading state when trying to categorize/label a search query (as seen in Dashboard) */
  isLoadingQueryLabelText?: boolean;
  /** Used to show whether or not the current query label is valid for the current search query value (as seen in Dashboard) */
  isQueryLabelValid?: boolean;
  /** The label (<Note /> component) to use for the current query (if applicable) */
  queryLabelText?: string;
  /** Boolean to indicate when the form values are currently being submitted */
  submitting?: boolean;
  /** Value of the input, aka the 'search term' */
  value: string;
  /** Custom id for SearchInput */
  id?: string;
  /** Custom placeholder text for SearchInput */
  placeholder?: string;
  /** Handler to use when the input value changes */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Handler for when the search is submitted */
  onSubmit: (query: string) => void;
}

export const SearchInput: React.FC<Props> = ({
  className,
  label = 'Search',
  isLoadingQueryLabelText,
  submitting,
  queryLabelText,
  isQueryLabelValid = true,
  onChange,
  onSubmit,
  placeholder,
  value,
  id,
  ...restProps
}) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (submitting) {
      setHasSubmitted(true);
    }
    if (isLoadingQueryLabelText) {
      setHasSubmitted(false);
    }
  }, [submitting, isLoadingQueryLabelText]);

  useEffect(() => {
    setHasSubmitted(false);
  }, [value]);

  const uniqueId = useUniqueId('searchInput');

  return (
    <div className={className}>
      <TextInput
        {...restProps}
        inputClassName={styles.searchTextInput}
        aria-label='search query'
        placeholder={placeholder}
        prefix={() => <Search className={styles.searchIcon} />}
        label={label}
        id={id || uniqueId}
        onChange={onChange}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            onSubmit(value);
          }
        }}
        value={value}
        iconButtons={[
          ...(isLoadingQueryLabelText
            ? [
                <LoadingSpinnerNoBits
                  className={styles.spinnerContainer}
                  spinnerClassName={styles.spinner}
                />,
              ]
            : queryLabelText
            ? [
                <Note
                  className={styles.searchQueryLabel}
                  success={isQueryLabelValid}
                  error={!isQueryLabelValid}
                  small
                >
                  {queryLabelText}
                </Note>,
              ]
            : []),
          <Button
            className={styles.searchButton}
            secondary
            showLoader
            size={32}
            textClassName={cx(submitting && styles.textLoadingState)}
            centered
            inline
            onClick={() => {
              onSubmit(value);
            }}
            submitting={submitting}
            aria-busy={submitting}
          >
            <span className={styles.visuallyHidden}>Perform</span> Search
          </Button>,
        ]}
        iconButtonWrapperClassName={styles.iconButtonWrapper}
      />
      {/* The below div is for accessibility announcements only - it won't display anything on screen */}
      <div
        role='region'
        aria-live='assertive'
        className={styles.visuallyHidden}
      >
        {queryLabelText
          ? `Current query ${value} matches label ${queryLabelText}.`
          : ''}
        {submitting
          ? `Searching for ${value}...`
          : hasSubmitted
          ? `Search for ${value} complete.`
          : ''}
      </div>
    </div>
  );
};

SearchInput.displayName = 'SearchInput';
export default SearchInput;
export { styles };
